import { ApiError, apiErrorReason } from '@/lib/api';

import type { LmsSync, LmsSyncKind, LmsSyncState } from './types';

// Envíos del documento al DMS del LMS en la ficha (G3-4 creación desde la plantilla, G3-5
// baja). Puro: estado en palabras, el motivo de una falla o de un envío omitido y si
// Calidad puede reintentarlo. Cero valores internos en pantalla (UX-COPY-01).

export interface SyncRow {
  kind: LmsSyncKind;
  label: string;
  badge: { text: string; tone: 'b-info' | 'b-ok' | 'b-warn' | 'b-red' | 'b-mut' };
  detail: string | null;
  updatedAt: string;
  retryable: boolean;
}

const LABELS: Record<LmsSyncKind, string> = {
  crear: 'Creación en el LMS',
  // La baja del documento y el documento creado que no quedó vinculado (G1-INT4).
  retirar: 'Retiro en el LMS',
};

const DONE: Record<LmsSyncKind, string> = {
  crear: 'Se creó el documento en el LMS desde la plantilla y quedó vinculado.',
  retirar: 'El documento quedó retirado en el LMS.',
};

const NOTES: Record<string, string> = {
  link_kept:
    'Se creó en el LMS, pero el documento ya tenía otro archivo vinculado y se mantuvo ese; el creado se retira automáticamente.',
  template_unavailable:
    'La plantilla del tipo no estaba vigente o no tenía archivo en el LMS. Vincula el archivo a mano.',
  entry_unavailable:
    'El documento se anuló o se dio de baja y no llegó a crearse en el LMS: no hay nada que retirar.',
  no_lms_document: 'El documento no tenía archivo del LMS vinculado: no había nada que retirar.',
  relinked: 'Se volvió a vincular ese archivo del LMS al documento: no se retira.',
  entry_retired: 'El documento se dio de baja: el que se creó en el LMS se retira automáticamente.',
  entry_annulled:
    'El documento se anuló: el borrador que se creó en el LMS se retira automáticamente.',
};

function rowOf(kind: LmsSyncKind, s: LmsSyncState): SyncRow {
  const base = { kind, label: LABELS[kind], updatedAt: s.updatedAt, retryable: s.retryable };
  const note = s.note === null ? null : (NOTES[s.note] ?? null);
  switch (s.status) {
    case 'pending':
      return {
        ...base,
        badge: { text: 'En curso', tone: 'b-info' },
        detail:
          kind === 'crear'
            ? 'Se está creando en el LMS desde la plantilla. La ficha se actualiza sola.'
            : 'Se envía automáticamente en unos minutos.',
      };
    case 'done':
      return {
        ...base,
        // Con otro archivo vinculado a mano no hay nada "hecho" que celebrar: se mantuvo
        // ese vínculo y el creado se retira solo (su retiro se ve en Retiro en el LMS).
        badge:
          s.note === 'link_kept'
            ? { text: 'Se mantuvo el vínculo', tone: 'b-info' }
            : { text: 'Hecho', tone: 'b-ok' },
        detail: note ?? DONE[kind],
      };
    case 'skipped':
      return { ...base, badge: { text: 'No se envió', tone: 'b-mut' }, detail: note };
    case 'failed':
      return {
        ...base,
        badge: s.retryable
          ? { text: 'Falló', tone: 'b-red' }
          : { text: 'Reintentando', tone: 'b-warn' },
        detail: s.failureReason ?? 'El LMS rechazó el envío. Revisa el documento y reintenta.',
      };
  }
}

/**
 * El LMS está creando el documento desde la plantilla (en curso, o reintentando solo).
 * Recorrido 2026-11-04: mientras tanto la ficha lo dice y se refresca sola, en vez de
 * mostrar «Sin vincular» como si fuera un error.
 */
export function isCreatingInLms(sync: LmsSync | undefined): boolean {
  const crear = sync?.crear ?? null;
  if (crear === null) {
    return false;
  }
  return crear.status === 'pending' || (crear.status === 'failed' && !crear.retryable);
}

/** Filas de la tarjeta: creación primero y baja después; sin las que no ocurrieron. */
export function syncRows(sync: LmsSync): SyncRow[] {
  const rows: SyncRow[] = [];
  if (sync.crear !== null) {
    rows.push(rowOf('crear', sync.crear));
  }
  if (sync.retirar !== null) {
    rows.push(rowOf('retirar', sync.retirar));
  }
  return rows;
}

/** Mensaje accionable para un reintento rechazado. */
export function retryError(err: unknown): string {
  const reason = apiErrorReason(err);
  if (reason === 'sync_not_failed') {
    return 'El envío ya no está fallido. Recarga la ficha para ver su estado.';
  }
  if (reason === 'template_unavailable') {
    return 'El tipo no tiene una plantilla vigente con archivo en el LMS. Asígnala en Catálogos o vincula el archivo a mano.';
  }
  if (reason === 'sync_in_progress' && err instanceof ApiError) {
    return err.message;
  }
  if (err instanceof ApiError && err.status === 403) {
    return 'No tienes permiso para esta acción. Pide acceso al Administrador de Calidad.';
  }
  return 'No se pudo reintentar. Intenta de nuevo.';
}
